// src/i18n/i18n.tsx
import React, { createContext, useContext, useMemo, useState, useCallback } from "react";
import type { Lang } from "./translations";
import { translations } from "./translations";

const LANG_KEY = "ovylon_lang";

type I18nContextValue = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, fallback?: string) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function safeGetInitialLang(): Lang {
  try {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(LANG_KEY) : null;
    if (saved === "es" || saved === "en") return saved;

    const navLang =
      typeof navigator !== "undefined" && typeof navigator.language === "string"
        ? navigator.language.toLowerCase()
        : "en";

    return navLang.startsWith("es") ? "es" : "en";
  } catch {
    return "en";
  }
}

function getNested(obj: any, path: string): any {
  return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, _setLang] = useState<Lang>(() => safeGetInitialLang());

  const setLang = useCallback((next: Lang) => {
    _setLang(next);
    try {
      window.localStorage.setItem(LANG_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const t = useCallback(
    (key: string, fallback?: string) => {
      const fromLang = getNested(translations[lang], key);
      if (typeof fromLang === "string") return fromLang;

      const fromEn = getNested(translations.en, key);
      if (typeof fromEn === "string") return fromEn;

      return fallback ?? key;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider />");
  return ctx;
}
